import { Theme } from "@mui/material";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import React, { forwardRef, RefAttributes } from "react";
import MenuIconButton from "./MenuIconButton";

export interface Props {
    icon?: React.ElementType | React.ReactNode;
    iconRight?: React.ElementType;
    active?: boolean;
    grow?: boolean;
    label?: string;
    tooltip?: string;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    className?: string;
    testId?: string;
    iconClassName?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
    label: {
        textTransform: "none"
    },
    button: {
        transition: theme.transitions.create(["background-color", "border-color"]),
        margin: "0.25rem",
        borderColor: "rgba(0, 0, 0, 0.54)",
        color: "rgba(0, 0, 0, 0.87)",
        whiteSpace: "nowrap",
        flexShrink: 0,
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            borderColor: "rgba(0, 0, 0, 0.87)",
            color: "rgba(0, 0, 0, 0.87)"
        }
    },
    buttonActive: {
        borderColor: "rgba(0, 0, 0, 0.87) !important",
        color: "white !important",
        backgroundColor: "rgba(0, 0, 0, 0.87) !important",
        pointerEvents: "none"
    },
    buttonGrow: {
        flexGrow: 1
    },
    tooltip: {
        backgroundColor: "rgba(0,0,0,0.87)"
    },
    arrow: {
        color: "rgba(0,0,0,0.87)"
    }
}));

type MenuButtonType = React.FC<Props & RefAttributes<HTMLButtonElement>>;
const MenuButton: MenuButtonType = forwardRef<HTMLButtonElement, Props>((props, ref) => {
    const classes = useStyles();

    const icon = React.isValidElement(props.icon)
        ? props.icon
        : React.createElement((props.icon as React.ElementType), {
            className: props.iconClassName
        });

    const button = props.label === undefined ? (
        <MenuIconButton
            data-cy={props.testId}
            ref={ref}
            onClick={props.onClick}
            icon={icon}
            className={clsx(
                classes.button,
                props.grow && classes.buttonGrow,
                props.active && classes.buttonActive,
                props.className
            )}>

            {props.label}

        </MenuIconButton>
    ) : (
        <Button
            data-cy={props.testId}
            color="primary"
            ref={ref}
            disableElevation
            variant="outlined"
            onClick={props.onClick}
            classes={{ root: classes.label }}
            startIcon={icon}
            endIcon={props.iconRight && React.createElement(props.iconRight)}
            className={clsx(
                classes.button,
                props.grow && classes.buttonGrow,
                props.active && classes.buttonActive,
                props.className
            )}>

            {props.label}

        </Button>
    );

    if (props.tooltip) {
        return (
            <Tooltip
                arrow
                placement="top"
                title={props.tooltip}
                classes={{
                    tooltip: classes.tooltip,
                    arrow: classes.arrow
                }}>

                {button}

            </Tooltip>
        );
    }

    return button;
});

export default MenuButton;
